import useLogger from 'lib/util/useLogger'
import constants from './constants'
import fetchJSONData from './util/fetchJSONData'
import { getGTM } from './util/tagManagerUtils'

const debug = useLogger('Tag Manager', 'teal')

/**
 * The `TagManager` handles Google Tag Manager events across
 * the application. It should only be initialized as part of the
 * consent provided to the `cookiesEvents` utility
 */
class TagManager {
  /** @type {boolean} */
  isInitialized = false

  /** @type {object} */
  walletAddress = null

  /**
   * Initialize
   *
   * @param {object} config - the gtm config to init with
   * @public
   */
  initialize (config = {}) {
    const {
      gtmId,
      events = {},
      dataLayer,
      dataLayerName = 'dataLayer',
      auth = '',
      preview = ''
    } = config

    if (this.isInitialized) {
      // Defensive
      return
    }

    const gtm = getGTM({
      id: gtmId,
      events,
      dataLayer: dataLayer || undefined,
      dataLayerName,
      auth,
      preview
    })

    if (dataLayer) {
      document.head.appendChild(gtm.dataScript)
    }

    document.head.insertBefore(gtm.script(), document.head.childNodes[0])
    document.body.insertBefore(gtm.noScript(), document.body.childNodes[0])
    this.isInitialized = true
  }

  /**
   * Set the wallet address
   *
   * @param {string} walletAddress - the wallet address
   */
  setWalletAddress (walletAddress = '') {
    this.walletAddress = walletAddress
  }

  /**
   * Track an event with our own API
   *
   * @param {object} props - the input props
   * @param {object} props.action - the action object, containing relevant data
   * @param {string} props.event - the event name
   */
  trackAPIEvent ({ action, event }) {
    if (constants.IS_DEV) {
      // Ignore when on dev mode
      return
    }

    fetchJSONData(
      `${constants.API_V2_URL}/frontend/tracking/user_activity`,
      {
        method: 'POST',
        body: JSON.stringify({
          wallet: this.walletAddress || '',
          network: constants.NETWORK,
          event: `app.${event}`,
          action
        }),
        headers: {
          'company-id': constants.API_ID,
          'Content-Type': 'application/json'
        }
      },
      false
    )
  }

  /**
   * Tracks a custom app event. All events are called to
   * a single event trigger (to reduce manual work for adding
   * new events on the fly)
   *
   * @param {object} props - the input props
   * @param {string} props.event - the event name
   * @param {object|string} props.action - the action prop
   * @public
   */
  trackEvent ({ event = '', action = {} }) {
    if (!this.isInitialized) {
      // Not initialized (no consent)
      return
    }

    this.trackAPIEvent({ event, action })

    if (!window.dataLayer) {
      // Defensive when GTM not ready
      return
    }

    window.dataLayer.push({ event: `app.${event}`, action })
    // Debug
    debug.log('track event:', { event: `app.${event}`, action })
  }
}

// Export as instance
export default new TagManager()
